import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { InputForm } from '../components/SmsChecker/InputForm';
import { ResultDisplay } from '../components/SmsChecker/ResultDisplay';
import { DevModeInjector } from '../components/SmsChecker/DevModeInjector';
import { analyzeSms, type SmsAnalyzeResponse } from '../services/api';

export function SmsAnalyze() {
  const { t } = useTranslation();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SmsAnalyzeResponse | null>(null);
  const [error, setError] = useState('');
  const [injectedContent, setInjectedContent] = useState('');

  const handleAnalyze = async (content: string) => {
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await analyzeSms({ content });
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Check if content was passed from Home page navigation
  useEffect(() => {
    const state = location.state as { content?: string } | null;
    if (state?.content) {
      setInjectedContent(state.content);
      // Auto-analyze if content was provided
      handleAnalyze(state.content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleInjectTestCase = (content: string) => {
    setInjectedContent(content);
    setResult(null);
    setError('');
  };

  return (
    <div className="page-container">
      <div className="content-wrapper--medium">
        <div className="mb-4 flex items-center justify-end">
          <LanguageSwitcher />
        </div>
        <h1 className="page-title--compact">{t('sms.title')}</h1>
        <p className="text-gray-600 mb-8">{t('sms.description')}</p>

        <div className="card mb-6">
          <InputForm 
            onSubmit={handleAnalyze} 
            isLoading={loading}
            initialContent={injectedContent}
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        {result && (
          <div className="card">
            <ResultDisplay result={result} />
          </div>
        )}
      </div>

      <DevModeInjector onInjectTestCase={handleInjectTestCase} />
    </div>
  );
}
